import { usePostUploadsByTripByTripIdGenerateUrlsMutation } from '../store/backendApi';
import { useDispatch } from 'react-redux';
import { showError } from '../store/errorSlice';

export interface FileUpload {
  uploadUrl: URL;
  localFile?: File;
  dimensions: { width: number; height: number };
  uploadId: string;
}

export interface FileUploadResult {
  success: boolean;
  error?: unknown;
  fileUpload?: FileUpload;
}

export interface FileUploadParameters {
  tripId: string;
  storyId?: string;
  files: File[];
  onUploaded: (uploadResult: FileUploadResult) => void | Promise<void>;
}

export const useUploadFiles = () => {
  const [generateUploadUrls] = usePostUploadsByTripByTripIdGenerateUrlsMutation();
  const dispatch = useDispatch();

  const getImageDimensions = async (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getPresignedUrls = async (tripId: string, files: File[], storyId?: string) => {
    const presignedUrls = await generateUploadUrls({
      tripId: tripId,
      mediaUploadRequest: {
        storyId: storyId,
        filenames: files.map((file) => {
          return { filename: file.name };
        }),
      },
    }).unwrap();
    if (!presignedUrls) {
      throw new Error('could not get upload URI');
    }
    return presignedUrls;
  };

  const uploadFile = async (file: File, uploadUrl: string) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    if (!response.ok) {
      throw new Error(`upload failed with code ${response.status}`);
    }
  };

  const uploadFilesHook = async ({ tripId, storyId, files, onUploaded }: FileUploadParameters) => {
    const mediaUploadResponse = await getPresignedUrls(tripId, files, storyId);
    await Promise.all(
      files.map(async (file) => {
        try {
          const uploadUrlObj = mediaUploadResponse.uploadUrls.find(
            (urlObj) => urlObj.filename === file.name,
          );
          if (!uploadUrlObj) {
            throw new Error('upload url invalid');
          }
          await uploadFile(file, uploadUrlObj.uploadUrl);

          const dim = await getImageDimensions(file);

          onUploaded({
            success: true,
            fileUpload: {
              uploadUrl: new URL(uploadUrlObj.uploadUrl),
              uploadId: uploadUrlObj.uploadId,
              localFile: file,
              dimensions: dim,
            },
          });
        } catch (e) {
          dispatch(showError('Failed to upload file. Please try again.'));
          onUploaded({ success: false, error: e });
        }
      }),
    );
  };

  return { uploadFiles: uploadFilesHook };
};
